// Group formation slotting.
//
// A group move order previously handed *every* selected squad the identical targetPos
// (sim.ts's IssueCommand loop), so an army converged on one point and its final shape was
// whatever crowd separation happened to produce: a blob that arrives as a smear, with fast
// squads first and slow ones trailing.
//
// This assigns each squad its own destination within a formation anchored at the order point
// and facing along the direction of travel.
//
// DETERMINISM
// This runs inside command application, which the authoritative server executes too, so it must
// produce identical output on both sides. Three rules make that true by construction:
//
//   1. Squad ids are sorted before anything else. The command carries the client's selection
//      order, which is not meaningful and not guaranteed to match between peers.
//   2. Slot ordering is a total order over data only (size rank, ranged flag, defId, id) — no
//      floats decide ties, and no faction ids appear.
//   3. The centroid is summed in that same sorted order, so its floating-point accumulation is
//      identical everywhere.

import type { Sim } from '../sim';
import type { Squad } from '../state';
import type { EntId } from '../state';
import type { Vec2 } from '../../shared/vec';
import { unitDef } from '../../content/registry';

/** Row spacing multiplier over the pair's separation radius, so ranks are not shoulder-locked. */
const SPACING = 2.5; // TEST
/** Formations wider than they are deep read as a battle line rather than a column. */
const ASPECT = 1.6; // TEST
/**
 * Below this many squads a formation is noise — two squads should just go where you clicked.
 * Also skipped for very short moves, where re-slotting looks like the army shuffling in place.
 */
const MIN_SQUADS = 3;
const MIN_DISTANCE = 6; // TEST

const SIZE_RANK: Record<string, number> = { small: 0, medium: 1, large: 2, giant: 3 };
const SEP_RADIUS: Record<string, number> = { small: 0.5, medium: 0.7, large: 1.3, giant: 2.5 };

/**
 * Front-to-back ordering key. Melee and small units take the front ranks, ranged and siege sit
 * behind them, giants anchor the rear — all derived from existing unit data, so no faction
 * branches and no new content fields.
 */
function orderKey(s: Squad): [number, number, string, EntId] {
  const d = unitDef(s.defId);
  const ranged = d.roleTags.includes('ranged') || (d.attack?.range ?? 0) > 8 ? 1 : 0;
  const siege = d.roleTags.includes('siege') ? 1 : 0;
  return [ranged + siege, SIZE_RANK[d.sizeTag] ?? 1, d.id, s.id];
}

function compareOrderKey(a: [number, number, string, EntId], b: [number, number, string, EntId]): number {
  return a[0] - b[0] || a[1] - b[1] || a[2].localeCompare(b[2]) || a[3] - b[3];
}

export interface FormationAssignment {
  squad: Squad;
  target: Vec2;
}

/**
 * Compute per-squad destinations for a group order.
 *
 * Returns null when a formation is inappropriate (too few squads, too short a move), in which
 * case the caller keeps the existing "everyone to the same point" behaviour.
 */
export function planFormation(sim: Sim, squads: Squad[], target: Vec2): FormationAssignment[] | null {
  if (squads.length < MIN_SQUADS) return null;

  // Rule 1: canonical order first. Everything downstream depends on this.
  const sorted = [...squads].sort((a, b) => a.id - b.id);

  // Rule 3: centroid summed in the canonical order.
  let cx = 0;
  let cy = 0;
  for (const s of sorted) {
    cx += s.pos.x;
    cy += s.pos.y;
  }
  cx /= sorted.length;
  cy /= sorted.length;

  const dx = target.x - cx;
  const dy = target.y - cy;
  const travel = Math.hypot(dx, dy);
  if (travel < MIN_DISTANCE) return null;

  // Facing along the direction of travel, so the formation arrives broadside-on to the
  // destination rather than in a column.
  const fx = dx / travel;
  const fy = dy / travel;
  // Right vector (perpendicular), for the across-rank axis.
  const rx = -fy;
  const ry = fx;

  // Rule 2: ranks assigned by a data-only total order.
  const ranked = [...sorted].sort((a, b) => compareOrderKey(orderKey(a), orderKey(b)));

  const count = ranked.length;
  const cols = Math.max(1, Math.round(Math.sqrt(count * ASPECT)));
  // Spacing from the widest member so giants do not overlap their neighbours.
  let maxRadius = 0.7;
  for (const s of ranked) maxRadius = Math.max(maxRadius, SEP_RADIUS[unitDef(s.defId).sizeTag] ?? 0.7);
  const step = maxRadius * SPACING;

  const rows = Math.ceil(count / cols);
  const out: FormationAssignment[] = [];
  for (let index = 0; index < count; index++) {
    const row = Math.floor(index / cols);
    const col = index % cols;
    // Centre each row, including a short final row.
    const inThisRow = Math.min(cols, count - row * cols);
    const across = (col - (inThisRow - 1) / 2) * step;
    // Row 0 is the front rank; later rows fall in behind it along -facing. Centred on the order
    // point so the block straddles where the player clicked rather than starting there.
    const depth = ((rows - 1) / 2 - row) * step;

    const slotX = target.x + rx * across + fx * depth;
    const slotY = target.y + ry * across + fy * depth;

    // Never hand out a destination inside terrain — the squad would path to it, fail, and be
    // recovered by the stuck logic. Fall back to the raw order point, which the existing
    // command validation already accepted.
    const walkable = sim.nav.isWalkable({ x: slotX, y: slotY }, ranked[index].owner);
    out.push({
      squad: ranked[index],
      target: walkable ? { x: slotX, y: slotY } : { x: target.x, y: target.y },
    });
  }
  return out;
}
